import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../App';

const useLogger = () => {
  const [isTracking, setIsTracking] = useState(true);
  const sessionStartTime = useRef(Date.now());
  const lastActionTime = useRef(Date.now());
  const currentPage = useRef('unknown');
  const actionHistory = useRef([]);

  // Track page visit (only if different from current page)
  const trackPageVisit = (pageName) => {
    if (!isTracking) return;
    
    // Skip if it's the same page (avoid duplicates)
    if (currentPage.current === pageName) {
      return;
    }
    
    const now = Date.now();
    const duration = now - lastActionTime.current;
    
    currentPage.current = pageName;
    lastActionTime.current = now;
    
    // Track in backend
    trackAction('page_visit', {
      page: pageName,
      duration,
      previousAction: actionHistory.current[actionHistory.current.length - 1] || '',
      nextAction: 'page_loaded'
    });
    
    actionHistory.current.push('page_visit');
  };

  // Track user action
  const trackAction = async (action, details = {}) => {
    if (!isTracking) return;
    
    const now = Date.now();
    const duration = now - lastActionTime.current;
    
    lastActionTime.current = now;
    
    const actionData = {
      action,
      page: currentPage.current,
      duration,
      previousAction: actionHistory.current[actionHistory.current.length - 1] || '',
      nextAction: details.nextAction || '',
      ...details
    };
    
    // Add to history
    actionHistory.current.push(action);
    
    // Keep only last 10 actions
    if (actionHistory.current.length > 10) {
      actionHistory.current = actionHistory.current.slice(-10);
    }
    
    try {
      await axios.post(`${getApiUrl()}/logs/track`, actionData);
    } catch (error) {
      console.warn('Failed to track action:', error);
    }
  };

  // Track student interaction
  const trackStudentInteraction = (studentTC, studentName, action = 'student_clicked') => {
    const now = Date.now();
    const duration = now - lastActionTime.current;
    lastActionTime.current = now;
    
    trackAction(action, {
      studentTC,
      studentName,
      duration,
      nextAction: 'student_detail_viewed'
    });
  };

  // Track filtering action
  const trackFilter = (filterType, filterValue) => {
    trackAction('filter_applied', {
      filterType,
      filterValue,
      nextAction: 'filtered_results'
    });
  };

  // Track file upload
  const trackFileUpload = (fileName, fileSize) => {
    trackAction('file_upload', {
      fileName,
      fileSize,
      nextAction: 'file_processed'
    });
  };

  // Track button click
  const trackButtonClick = (buttonName, page = null) => {
    trackAction('button_click', {
      buttonName,
      page: page || currentPage.current,
      nextAction: 'action_triggered'
    });
  };

  // Removed tracking functions: search_performed, navigation, time_spent

  // Get session duration
  const getSessionDuration = () => {
    return Date.now() - sessionStartTime.current;
  };

  // Enable/disable tracking
  const setTracking = (enabled) => {
    setIsTracking(enabled);
  };

  // Removed page unload tracking (time_spent)

  return {
    trackPageVisit,
    trackAction,
    trackStudentInteraction,
    trackFilter,
    trackFileUpload,
    trackButtonClick,
    getSessionDuration,
    setTracking,
    isTracking,
    currentPage: currentPage.current
  };
};

export default useLogger;
